import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Actions, Card, Table, useNotify, Loader, Space, Icon, Button } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { ConfirmModal, Filters } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Plus } from 'resources/icons';
import { FormModal } from '../FormModal';
import { FormNomenclaturesCities } from '../FormNomenclatures';
import { Results, Column, FilterType, Nomenclatures } from 'types';
import { useCitiesAPI } from 'api';
import { defaultFilters } from 'utils';
import { useSetState } from 'react-use';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';

const formId = 'city';

interface FiltersProps {
  search?: string;
  page?: number;
}

export const NomenclaturesCities: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateQuery } = useQueryUpdate();
  const { getCities, createCity, getCityById, updateCity, deleteCity } = useCitiesAPI();

  const [isCreate, setIsCreate] = React.useState<boolean>(false);
  const [editItem, setEditItem] = React.useState();
  const [deleteItem, setDeleteItem] = React.useState();

  const hasEditPermission = useRolePermission([UserRoleCRM.admin]);

  const { data: city, isLoading } = useQuery<Results<Nomenclatures>>(
    ['city', { ...defaultFilters, ...params }],
    getCities,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const deleteCitiesMutation = useMutation(deleteCity, {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      queryClient.invalidateQueries(['city']);
      setDeleteItem(undefined);
      notify.success({ title: t('success.successDataDelete') });
    },
  });

  const handleDelete = (): void => {
    if (deleteItem) {
      deleteCitiesMutation.mutate(deleteItem);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteItem(undefined);
  };

  const handleCloseModal = (): void => {
    setEditItem(undefined);
    setIsCreate(false);
  };

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const [filters, setFilters] = useSetState<FiltersProps>({
    ...params,
    page: params.page ? Number(params.page) : 1,
  });

  React.useEffect(() => {
    const { ...rest } = filters;

    updateQuery({
      ...rest,
      per_page: params.per_page,
    });
  }, [filters]);

  const handleFilters = (value): void => {
    setFilters({ ...value, page: 1 });
  };

  const columns: Column<Nomenclatures>[] = React.useMemo(
    () => [
      {
        title: t('contacts.city'),
        onFilter: (type) => handleFilter(type, 'name'),
        render: ({ name }): React.ReactNode => name,
      },
      {
        title: t('contacts.region'),
        onFilter: (type) => handleFilter(type, 'region__name'),
        render: ({ region }): React.ReactNode => region?.name,
      },
      ...(hasEditPermission
        ? [
            {
              align: 'right',
              render: ({ id }) => (
                <Space justify="end">
                  <Actions placement="bottom">
                    <Actions.Item onClick={() => setEditItem(id)}>
                      {t('nomenclature.edit')}
                    </Actions.Item>
                    <Actions.Item onClick={() => setDeleteItem(id)}>
                      {t('nomenclature.delete')}
                    </Actions.Item>
                  </Actions>
                </Space>
              ),
            } as Column<Nomenclatures>,
          ]
        : []),
    ],
    [t, city, params],
  );

  return (
    <>
      <Space justify="space-between" className="mb-20">
        <Filters className="mb-0" onChange={handleFilters}>
          <Space justify="start">
            <h3 className="title">{`${t(`nomenclature.cities`)}${
              city?.count ? ` (${city.count})` : ``
            }`}</h3>

            <Filters.Search field="search" value={params.search} placeholder={t('filter.search')} />
          </Space>
        </Filters>

        {hasEditPermission && (
          <Button
            type="primary"
            prefix={<Icon component={Plus} />}
            onClick={() => setIsCreate(true)}
          >
            {t('buttons.addNew')}
          </Button>
        )}
      </Space>

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={city?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={city?.total_pages || 0} />
        </Card.Footer>
      </Card>

      {!!deleteItem && <ConfirmModal onSuccess={handleDelete} onCancel={handleCancelDelete} />}

      {(editItem || isCreate) && (
        <FormModal formId={formId} onCancel={handleCloseModal} isCreate={isCreate}>
          <FormNomenclaturesCities
            formId={formId}
            getItem={getCityById}
            onFinish={isCreate ? createCity : updateCity}
            onClose={handleCloseModal}
            id={editItem}
          />
        </FormModal>
      )}
    </>
  );
};
